import React, { useEffect, useState } from "react";
import { database } from "../firebaseConfig";
import { ref, update, onValue } from "firebase/database";
import "./SpamCheckSettings.css";

const OPTIONS = [
  {
    id: "rules",
    icon: "fa-gauge-high",
    eyebrow: "Default · Free · Instant",
    title: "Standard Check",
    desc: "A fast, built-in check that scans every email you generate for spam trigger words and salesy phrasing. Runs live as you type, right next to the Send button — no extra cost, no waiting.",
    points: [
      "Flags promotional trigger words & aggressive sales language",
      "Catches ALL-CAPS, excessive punctuation and fake “Re:/Fwd:”",
      "Instant — updates while you write",
    ],
  },
  {
    id: "ai",
    icon: "fa-wand-magic-sparkles",
    eyebrow: "AI-powered · Deeper analysis",
    title: "SalesGod Spam Analyzer",
    desc: "Reads each generated email the way a strict inbox (Gmail, Microsoft 365) would and gives a holistic spam verdict — not just single words, but tone and overall “feel”. In bulk mode it runs in the background while emails are pre-generated, so the result is ready the moment you open a contact.",
    points: [
      "Understands tone & context, not just keywords",
      "Explains exactly why an email looks risky",
      "Pre-computed in the background for bulk sending",
    ],
  },
];

const SpamCheckSettings = ({ userId }) => {
  const [mode, setMode] = useState("rules");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!userId) return undefined;
    const settingsRef = ref(database, `/World/${userId}/settings/spamCheckSettings`);
    const unsub = onValue(settingsRef, (snap) => {
      const val = snap.val();
      setMode(val && val.mode === "ai" ? "ai" : "rules");
    });
    return () => unsub();
  }, [userId]);

  const selectMode = async (newMode) => {
    if (!userId || newMode === mode) return;
    setMode(newMode);
    try {
      await update(ref(database, `/World/${userId}/settings/spamCheckSettings`), { mode: newMode });
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (e) {
      console.error("Failed to save spam check mode:", e);
    }
  };

  const activeOption = OPTIONS.find((o) => o.id === mode);

  return (
    <div className="sa">
      <header className="sa-page-head">
        <div className="sa-page-head-main">
          <div>
            <h3 className="sa-page-title">Spam Analyzer</h3>
            <p className="sa-page-sub">
              Choose how BonusClub warns you before you send an email that might land in spam or promotions.
              The warning sits next to Send in Normal and Bulk.
            </p>
          </div>
        </div>
        <span className={`sa-badge ${saved ? "" : "off"}`}>
          {saved ? (
            <><i className="fa fa-check" /> Saved</>
          ) : (
            activeOption?.title || "Standard Check"
          )}
        </span>
      </header>

      <div className="sa-cols">
        {OPTIONS.map((opt) => {
          const active = mode === opt.id;
          return (
            <button
              type="button"
              key={opt.id}
              className={`sa-card ${active ? "is-active" : ""}`}
              onClick={() => selectMode(opt.id)}
            >
              <div className="sa-card-top">
                <span className="sa-icon" aria-hidden>
                  <i className={`fa ${opt.icon}`} />
                </span>
                <span className={`sa-radio ${active ? "is-on" : ""}`}>
                  {active && <i className="fa fa-check" />}
                </span>
              </div>
              <div>
                <span className="sa-card-eyebrow">{opt.eyebrow}</span>
                <h4 className="sa-card-title">{opt.title}</h4>
              </div>
              <p className="sa-card-desc">{opt.desc}</p>
              <ul className="sa-points">
                {opt.points.map((p) => (
                  <li key={p}><i className="fa fa-circle-check" />{p}</li>
                ))}
              </ul>
              {active && <span className="sa-active-pill">Active</span>}
            </button>
          );
        })}
      </div>

      <div className="sa-note">
        <span className="sa-icon sa-note-icon" aria-hidden>
          <i className="fa fa-circle-info" />
        </span>
        <span>
          This only affects the live warning while you compose. It never blocks sending, and it doesn't run in
          fully-automated modes or outreach jobs (where you don't review each email).
        </span>
      </div>
    </div>
  );
};

export default SpamCheckSettings;
